import {
  Heading,
  Text,
  Button,
  Box,
  Tag,
  IconButton,
  Image,
} from '@everlywell/ui-kit';
import DynamicIcon from 'components/DynamicIcon';
import { useAsyncProgramPPQContext } from 'contexts/AsyncProgramPPQ/AsyncProgramPPQContext';
import React, { useEffect } from 'react';
import analytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';

import { QuizIdsEnum } from '../constants';
import bvMobileModalImage from './images/bv/mobile-ppq-modal-images.png';
import bvDesktopModalImage from './images/bv/ppq-modal-images.png';
import csMobileModalImage from './images/cs/mobile-ppq-modal-images.png';
import csDesktopModalImage from './images/cs/ppq-modal-images.png';
import ghMobileModalImage from './images/gh/mobile-ppq-modal-images.png';
import ghDesktopModalImage from './images/gh/ppq-modal-images.png';
import mobileModalImage from './images/mobile-ppq-modal-images.png';
import desktopModalImage from './images/ppq-modal-images.png';
import utiMobileModalImage from './images/uti/mobile-ppq-modal-images.png';
import utiDesktopModalImage from './images/uti/ppq-modal-images.png';
import yiMobileModalImage from './images/yi/mobile-ppq-modal-images.png';
import yiDesktopModalImage from './images/yi/ppq-modal-images.png';

type InitialQuizScreenContent = {
  title: string;
  description: JSX.Element;
  ctaText: string;
  desktopModalImage: string;
  mobileModalImage: string;
};

export type InitialQuizScreenProps = {
  quizId: QuizIdsEnum;
};

const DEFAULT_CONTENT = {
  title: 'Quickly find out if you’re eligible for a treatment',
  description: (
    <p>
      We’ll need to know some basic info about you, your symptoms, and your
      medical history. If you are having a medical emergency, please call 911 or
      seek in-person care immediately.
    </p>
  ),
  ctaText: 'Start assessment',
  mobileModalImage: mobileModalImage,
  desktopModalImage: desktopModalImage,
};

/**
 * This component adds the Initial Quiz Screen the pre-purchase quiz modal.
 */

// TODO:
// - move content to contentful and fetch it from there

function InitialQuizScreen({ quizId }: InitialQuizScreenProps): JSX.Element {
  const { startQuiz, toggleModal, resetState } = useAsyncProgramPPQContext();

  const setContent = (quizId: QuizIdsEnum): InitialQuizScreenContent => {
    let content = {} as InitialQuizScreenContent;

    switch (quizId) {
      case 'uti-async-pre-purchase-quiz':
        content = {
          ...DEFAULT_CONTENT,
          mobileModalImage: utiMobileModalImage,
          desktopModalImage: utiDesktopModalImage,
        };
        break;
      case 'bv-async-pre-purchase-quiz':
        content = {
          ...DEFAULT_CONTENT,
          mobileModalImage: bvMobileModalImage,
          desktopModalImage: bvDesktopModalImage,
        };
        break;
      case 'gh-async-pre-purchase-quiz':
        content = {
          ...DEFAULT_CONTENT,
          mobileModalImage: ghMobileModalImage,
          desktopModalImage: ghDesktopModalImage,
        };
        break;
      case 'yi-async-pre-purchase-quiz':
        content = {
          ...DEFAULT_CONTENT,
          mobileModalImage: yiMobileModalImage,
          desktopModalImage: yiDesktopModalImage,
        };
        break;
      case 'cs-async-pre-purchase-quiz':
        content = {
          ...DEFAULT_CONTENT,
          mobileModalImage: csMobileModalImage,
          desktopModalImage: csDesktopModalImage,
        };
        break;
      default:
        content = DEFAULT_CONTENT;
    }

    return content;
  };

  const content = setContent(quizId);

  const handleModalClose = () => {
    toggleModal(false);
    resetState();
  };

  useEffect(() => {
    analytics.track({
      event: ANALYTICS.EVENTS.VIEWED_COMPONENT,
      data: {
        label: ANALYTICS.PRE_PURCHASE_QUIZ.LABEL,
        step: ANALYTICS.PRE_PURCHASE_QUIZ.STEPS.INITIAL_SCREEN,
      },
    });
  }, []);

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="space-between"
      backgroundColor="sky.dark"
      textAlign="center"
      height="100vh"
      position="relative"
      paddingTop={[5, null, 0]}
      overflowY="hidden"
    >
      <IconButton
        aria-label="close"
        colorScheme="whiteCS"
        height="40px"
        icon={<DynamicIcon icon="X" color="tints.uiGrey" />}
        onClick={handleModalClose}
        position="absolute"
        right={['24px', null, '70px']}
        size="sm"
        top="20px"
        width="40px"
        zIndex={3}
      />
      <Box
        position="relative"
        zIndex={2}
        maxWidth={[null, '600px']}
        paddingX="5"
        paddingBlockStart="90px"
      >
        <Tag
          backgroundColor="moonlight.lighter"
          color="sky.dark"
          marginBottom={['8', '10']}
        >
          Quiz
        </Tag>
        {content.title && (
          <Heading
            as="h1"
            size="xl"
            color="tints.white"
            marginBottom={['8', '10']}
          >
            {content.title}
          </Heading>
        )}
        {content.description && (
          <Text as="div" color="tints.white" marginBottom={['8', '10']}>
            {content.description}
          </Text>
        )}
        {content.ctaText && (
          <Button
            variant="primary"
            size={['wide', 'md']}
            colorScheme="whiteCS"
            marginBottom={8}
            onClick={startQuiz}
          >
            {content.ctaText}
          </Button>
        )}
      </Box>

      <Image
        src={content.mobileModalImage}
        alt="Image of a hexagon pattern and a woman smiling"
        display={['block', null, 'none']} // Show this image on mobile. This is a temporary hack until we move the images to Contentful.
        maxWidth="100%"
      />
      <Image
        src={content.desktopModalImage}
        alt="Image of a hexagon pattern and a woman smiling"
        display={['none', null, 'block']} // Show this image on desktop. This is a temporary hack until we move the images to Contentful.
        position="absolute"
        bottom="0"
        width="100%"
      />
    </Box>
  );
}

export default InitialQuizScreen;
